<template>
  <article class="self-service-card wide-card society-science-panel">
    <div class="science-toolbar">
      <p class="service-hint">
        学会科学传播栏目：工作动态、期刊服务、科普基地、科普文章、科普视频、化石保护。
      </p>
      <div class="filter-chips" role="tablist" aria-label="科学传播分类">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          :class="{ active: activeKey === tab.key }"
          :aria-selected="activeKey === tab.key"
          @click="selectTab(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <div v-loading="loading" class="science-list">
      <button
        v-for="item in items"
        :key="item.entryId || item.title"
        type="button"
        class="science-item"
        @click="front.openContentDetail(item)"
      >
        <div class="science-item__meta">
          <span v-if="columnLabel(item)">{{ columnLabel(item) }}</span>
          <time v-if="item.date">{{ item.date }}</time>
        </div>
        <strong>{{ item.title }}</strong>
        <span v-if="itemExcerpt(item)" class="science-item__summary">{{ itemExcerpt(item) }}</span>
      </button>
      <p v-if="!loading && !items.length" class="muted-copy">
        暂无「{{ activeTab.label }}」已发布内容
      </p>
    </div>

    <div v-if="total > pageSize" class="science-pager">
      <button type="button" class="ghost-action compact" :disabled="pageNum <= 1" @click="changePage(pageNum - 1)">
        上一页
      </button>
      <span>{{ pageNum }} / {{ totalPages }}</span>
      <button
        type="button"
        class="ghost-action compact"
        :disabled="pageNum >= totalPages"
        @click="changePage(pageNum + 1)"
      >
        下一页
      </button>
    </div>
  </article>
</template>

<script>
import { listPublicCmsEntries, unwrapPublicPage } from '@/api/paleo'
import {
  SOCIETY_SCIENCE_TABS,
  SCIENCE_COLUMN_LABEL,
  buildScienceListParams,
  matchesScienceTab,
  scienceColumnLabel
} from '@/utils/scienceCategories'

export { SOCIETY_SCIENCE_TABS }

export default {
  name: 'SocietySciencePanel',
  inject: ['front'],
  data() {
    return {
      tabs: SOCIETY_SCIENCE_TABS,
      activeKey: 'base',
      items: [],
      total: 0,
      pageNum: 1,
      pageSize: 5,
      loading: false,
      loadSeq: 0
    }
  },
  computed: {
    activeTab() {
      return this.tabs.find(item => item.key === this.activeKey) || this.tabs[0]
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    }
  },
  watch: {
    '$route.query.cat': {
      immediate: true,
      handler(cat) {
        this.applyCatFromRoute(cat)
        this.pageNum = 1
        this.loadList()
      }
    },
    'front.activeServiceTab'(tab) {
      if (tab === 'science') {
        this.applyCatFromRoute(this.$route.query.cat)
        this.pageNum = 1
        this.loadList()
      }
    }
  },
  created() {
    this.applyCatFromRoute(this.$route.query.cat)
  },
  methods: {
    columnLabel(item) {
      return scienceColumnLabel(item) || SCIENCE_COLUMN_LABEL[item.columnCode] || ''
    },
    itemExcerpt(item) {
      const text = String((item && item.summary) || '').trim()
      if (!text || this.columnLabel(item) === text) {
        return ''
      }
      return text
    },
    applyCatFromRoute(cat) {
      const key = String(cat || '').trim()
      if (key && this.tabs.some(item => item.key === key)) {
        this.activeKey = key
      } else if (!key) {
        this.activeKey = 'base'
      }
    },
    selectTab(key) {
      if (this.activeKey === key) return
      this.activeKey = key
      this.pageNum = 1
      const query = { ...this.$route.query, tab: 'science', cat: key }
      this.$router.replace({ path: '/services', query }).catch(() => {})
      this.loadList()
    },
    changePage(page) {
      this.pageNum = page
      this.loadList()
    },
    async loadList() {
      const seq = ++this.loadSeq
      const tab = this.activeTab
      this.loading = true
      try {
        const params = buildScienceListParams(tab, this.pageNum, this.pageSize)
        const result = await listPublicCmsEntries(params)
        if (seq !== this.loadSeq) return
        const page = unwrapPublicPage(result, {
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        const rows = (page.rows || [])
          .map(row => this.front.mapCmsEntry(row))
          .filter(row => matchesScienceTab(row, tab))
        this.items = rows
        this.total = page.total
      } catch (error) {
        if (seq !== this.loadSeq) return
        this.items = []
        this.total = 0
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style scoped>
.society-science-panel {
  display: grid;
  gap: 16px;
}

.science-toolbar {
  display: grid;
  gap: 12px;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-chips button {
  border: 1px solid #d7dde5;
  background: #fff;
  color: var(--primary, #002b49);
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
}

.filter-chips button.active {
  border-color: var(--primary, #002b49);
  background: var(--primary, #002b49);
  color: #fff;
}

.science-list {
  display: grid;
  gap: 10px;
  min-height: 80px;
}

.science-item {
  display: grid;
  gap: 4px;
  text-align: left;
  width: 100%;
  border: 1px solid #e5e1da;
  background: #fcfaf7;
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.science-item:hover {
  border-color: rgba(0, 43, 73, 0.35);
}

.science-item__meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: #8a93a0;
  font-size: 12px;
}

.science-item strong {
  color: var(--primary, #002b49);
  font-size: 15px;
}

.science-item__summary {
  color: #5b6470;
  font-size: 13px;
  line-height: 1.5;
}

.science-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
</style>
